import { useCallback, useEffect, useRef, useState } from 'react';
import { useRightPanel } from '@/components/layout/workspace/rightPanelContext';
import { useTabId } from '@/components/layout/workspace/workspaceContext';
import { useScrollMemory } from '@/components/layout/workspace/useScrollMemory';
import {
  Badge,
  BulkActions,
  Button,
  Checkbox,
  Deadline,
  LoadMore,
  Money,
  ScrollTools,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusControl,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ZonedDateTime,
} from '@/components/ui';
import { panelOf, SAMPLE_NOW, STATUS_OPTIONS, type SampleRecord } from './model';
import { useRecords } from './recordsContext';

const SECTIONS = [
  { id: 'records', label: 'Записи' },
  { id: 'numbers', label: 'Деньги и сроки' },
  { id: 'details', label: 'Как попробовать' },
];
const BATCH = 8;

export function RecordList() {
  const { records, changeStatus } = useRecords();
  const panel = useRightPanel();
  const tabId = useTabId();
  const memoryKey = 'kaname.patterns.list.v1:' + tabId;
  const [count, setCount] = useState(() => {
    try {
      const n = Number(localStorage.getItem(memoryKey + ':count'));
      return Number.isInteger(n) ? Math.min(records.length, Math.max(24, n)) : 24;
    } catch {
      return 24;
    }
  });
  const viewport = useRef<HTMLDivElement>(null);
  useScrollMemory(viewport, memoryKey + ':scroll');
  useEffect(() => {
    try {
      localStorage.setItem(memoryKey + ':count', String(count));
    } catch {
      /* Без памяти. */
    }
  }, [count, memoryKey]);
  const [loading, setLoading] = useState<'idle' | 'loading' | 'error'>('idle');
  const [automatic, setAutomatic] = useState(false);
  const [failNext, setFailNext] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef(false);
  const visible = records.slice(0, count);
  const total = records.length;
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const loadMore = useCallback(() => {
    if (pending.current || count >= total) return;
    pending.current = true;
    setLoading('loading');
    timer.current = window.setTimeout(() => {
      pending.current = false;
      if (failNext) {
        setFailNext(false);
        setLoading('error');
        return;
      }
      setCount((n) => Math.min(total, n + BATCH));
      setLoading('idle');
    }, 900);
  }, [count, total, failNext]);

  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  const selectedVisible = visible.filter((r) => selected.has(r.id)).length;
  const allSelected = selectedVisible === visible.length;
  const clear = () => {
    setSelected(new Set());
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLElement>('[data-panel-frame=main] [aria-label="Выбрать все загруженные записи"]')
        ?.focus(),
    );
  };
  const totals = (currency: string) =>
    visible.filter((r) => r.currency === currency).reduce((sum, r) => sum + r.amount, 0);

  return (
    <main className="flex flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 1</p>
        <h2 data-panel-default-focus="" tabIndex={-1} className="type-title focus-visible:outline-none">
          Список записей
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-fg-muted">
          Свойства рядом со списком, выбор нескольких строк и подгрузка без скачков. Примерные данные на 23
          октября 2026 года.
        </p>
      </header>
      <ScrollTools sections={SECTIONS} />
      <section id="records" className="flex scroll-mt-16 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="mr-auto type-subheading">
            Записи <span className="text-fg-subtle">{total}</span>
          </h3>
          <Switch label="Подгружать при прокрутке" checked={automatic} onCheckedChange={setAutomatic} />
          <Switch label="Сбой следующей подгрузки" checked={failNext} onCheckedChange={setFailNext} />
        </div>
        <BulkActions count={selected.size} onClear={clear}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => changeStatus([...selected], 'paid')}
            disabled={records.filter((r) => selected.has(r.id)).every((r) => r.status === 'paid')}
          >
            Отметить оплаченными ({selected.size})
          </Button>
        </BulkActions>
        <div className="overflow-hidden rounded-lg border border-line bg-card">
          <Table
            aria-label="Примерный реестр"
            aria-busy={loading === 'loading'}
            className="table-fixed"
            viewportClassName="h-96 scroll-pt-9 overflow-y-auto"
            viewportRef={viewport}
            footer={
              <LoadMore
                state={count >= total ? 'complete' : loading}
                automatic={automatic}
                onLoad={loadMore}
                loaded={visible.length}
                total={total}
              />
            }
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Выбрать все загруженные записи"
                    checked={allSelected ? true : selectedVisible > 0 ? 'indeterminate' : false}
                    onCheckedChange={(checked) =>
                      setSelected(checked ? new Set(visible.map((r) => r.id)) : new Set())
                    }
                  />
                </TableHead>
                <TableHead>Запись</TableHead>
                <TableHead numeric className="w-32">
                  Сумма
                </TableHead>
                <TableHead className="w-40">Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((record) => (
                <TableRow
                  key={record.id}
                  data-record-id={record.id}
                  selected={selected.has(record.id) || panel.item?.id === record.id}
                >
                  <TableCell>
                    <Checkbox
                      aria-label={`Выбрать запись ${record.id}`}
                      checked={selected.has(record.id)}
                      onCheckedChange={(checked) => toggle(record.id, checked === true)}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      data-panel-trigger={record.id}
                      onClick={(e) => panel.open(panelOf(record), e.currentTarget)}
                      className="block w-full truncate rounded-xs py-1 text-left text-sm font-medium text-fg hover:text-brand-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    >
                      <span className="mr-2 font-mono text-xs text-fg-subtle">№ {record.id}</span>
                      {record.title}
                    </button>
                  </TableCell>
                  <TableCell numeric className="font-sans text-sm">
                    <Money minorUnits={record.amount} currency={record.currency} />
                  </TableCell>
                  <TableCell>
                    <StatusControl
                      value={record.status}
                      options={STATUS_OPTIONS}
                      onChange={(value) => changeStatus([record.id], value as SampleRecord['status'])}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading !== 'idle' &&
                Array.from({ length: BATCH }, (_, i) => (
                  <TableRow key={'pending-' + i} data-pending-row="" aria-hidden>
                    <TableCell>
                      <Skeleton still={loading === 'error'} className="size-4" />
                    </TableCell>
                    <TableCell>
                      <Skeleton still={loading === 'error'} className="w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton still={loading === 'error'} className="ml-auto w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton still={loading === 'error'} className="h-4 w-24 rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        <div
          className="flex flex-wrap justify-end gap-6 text-xs text-fg-muted"
          aria-label="Итоги загруженных записей"
        >
          <span>
            Итого RUB <Money total minorUnits={totals('RUB')} currency="RUB" />
          </span>
          <span>
            Итого USD <Money total minorUnits={totals('USD')} currency="USD" />
          </span>
        </div>
      </section>
      <NumberExamples />
      <section id="details" className="flex scroll-mt-16 flex-col gap-4 pb-8">
        <h3 className="type-heading">Как попробовать</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          {[
            [
              'Два окна',
              'Открой запись рядом и её свойства. Потяни границу: панель занимает место только внутри своего окна.',
            ],
            [
              'Возврат к месту',
              'Прокрути список, открой связанного человека и вернись на вкладку. Прокрутка и открытая панель сохранятся.',
            ],
            [
              'Можно передумать',
              'Измени поле или статус. Нажми «Отменить» в сообщении либо Ctrl+Z. Во время правки Escape отменяет только поле.',
            ],
          ].map(([title, text]) => (
            <div key={title} className="rounded-lg border border-line bg-card p-5">
              <h4 className="mb-2 type-subheading">{title}</h4>
              <p className="text-sm text-fg-muted">{text}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function NumberExamples() {
  const [choice, setChoice] = useState('moscow');
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zone = choice === 'local' ? local : 'Europe/Moscow';
  const label = choice === 'local' ? 'Местное' : 'МСК';
  return (
    <section id="numbers" className="flex scroll-mt-16 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <h3 className="mr-auto type-heading">Деньги и сроки</h3>
        <Select value={choice} onValueChange={setChoice}>
          <SelectTrigger aria-label="Часовой пояс" title={zone} className="w-48">
            <SelectValue>{choice === 'local' ? 'Местное' : 'МСК'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="moscow">МСК</SelectItem>
            <SelectItem value="local">Местное · {local}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5 text-sm">
          <h4 className="type-subheading">Сумма с валютой</h4>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-fg-muted">Начислено</span>
            <Money minorUnits={123456789} currency="RUB" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-fg-muted">Корректировка</span>
            <Money minorUnits={-125050} currency="RUB" />
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-line pt-4">
            <span className="type-subheading">Итого</span>
            <Money total minorUnits={123331739} currency="RUB" />
          </div>
        </div>
        <div className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
          <h4 className="type-subheading">Срок и время дежурства</h4>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-fg-muted">Оплата</span>
            <Deadline at="2026-10-26T15:00:00Z" now={SAMPLE_NOW} timeZone={zone} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-fg-muted">Согласование</span>
            <Deadline at="2026-10-22T15:00:00Z" now={SAMPLE_NOW} timeZone={zone} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
            <span className="text-sm text-fg-muted">Начало дежурства</span>
            <ZonedDateTime at="2026-10-23T13:00:00Z" timeZone={zone} label={label} />
          </div>
        </div>
      </div>
      <div>
        <Badge>Сроки показаны относительно 23.10.2026</Badge>
      </div>
    </section>
  );
}
